import { useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Grievance } from '@/types/supabase';
import { format } from 'date-fns';

interface GrievanceDetailsProps {
  grievance: Grievance;
  onGrievanceUpdated: () => void;
}

export const GrievanceDetails = ({ grievance, onGrievanceUpdated }: GrievanceDetailsProps) => {
  const [status, setStatus] = useState<Grievance['status']>(grievance.status);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, currentOrganization } = useAuth();
  const { logActivity } = useActivityLogger();

  const handleStatusUpdate = async () => {
    if (!user || !currentOrganization) return;
    
    try {
      setIsSubmitting(true);
      
      // Update grievance status
      const { error } = await supabase
        .from('grievances')
        .update({ status })
        .eq('id', grievance.id);
      
      if (error) {
        console.error('Error updating grievance:', error);
        toast.error('Failed to update grievance status');
        return;
      }
      
      // Add comment if provided
      if (comment.trim()) {
        const { error: commentError } = await supabase
          .from('grievance_comments')
          .insert([
            {
              grievance_id: grievance.id,
              user_id: user.id,
              comment,
              organization_id: currentOrganization.organization.id,
            },
          ]);
        
        if (commentError) {
          console.error('Error adding comment:', commentError);
          toast.error('Failed to add comment, but status was updated');
        }
      }
      
      // Log activity
      await logActivity({
        action: 'update',
        resourceType: 'grievance',
        resourceId: grievance.id,
        details: { status, hadComment: !!comment.trim() }
      });
      
      toast.success('Grievance updated successfully');
      onGrievanceUpdated();
    } catch (error) {
      console.error('Error in updating grievance:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const priorityStyles = {
      low: 'bg-green-100 text-green-800 hover:bg-green-100',
      medium: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
      high: 'bg-red-100 text-red-800 hover:bg-red-100',
    };
    
    return (
      <Badge className={priorityStyles[priority as keyof typeof priorityStyles]} variant="outline">
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium">Created</Label>
          <p className="text-sm">
            {format(new Date(grievance.created_at), 'PPpp')}
          </p>
        </div>
        <div>
          <Label className="text-sm font-medium">Priority</Label>
          <p className="text-sm">{getPriorityBadge(grievance.priority)}</p>
        </div>
      </div>
      
      <div>
        <Label className="text-sm font-medium">Title</Label>
        <p className="text-lg font-semibold">{grievance.title}</p>
      </div>
      
      <div>
        <Label className="text-sm font-medium">Description</Label>
        <div className="mt-1 p-3 bg-muted rounded-md text-sm">
          {grievance.description}
        </div>
      </div>
      
      <div className="pt-4 border-t">
        <Label className="text-sm font-medium">Update Status</Label>
        <div className="grid grid-cols-1 gap-4 mt-2">
          <Select value={status} onValueChange={(value) => setStatus(value as Grievance['status'])}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_review">In Review</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          
          <div>
            <Label htmlFor="comment" className="text-sm font-medium">Add Comment (Optional)</Label>
            <Textarea
              id="comment"
              placeholder="Add a comment about this status change"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div className="flex justify-end">
            <Button onClick={handleStatusUpdate} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Grievance'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
